import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Gift, Calendar, Hourglass, ArrowRight, RefreshCw, History } from 'lucide-react';
import { toast } from 'sonner';
import { isAdmin, isHR } from '@/lib/auth';
import api from '@/lib/api';

const STATUS_BADGE = {
  available: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100',
  redeemed: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
  converted_to_cl: 'bg-violet-100 text-violet-700 hover:bg-violet-100',
  expired: 'bg-slate-100 text-slate-500 hover:bg-slate-100',
};

const STATUS_LABEL = {
  available: 'Available',
  redeemed: 'Redeemed',
  converted_to_cl: 'Converted to CL',
  expired: 'Expired',
};

// ─────────────────────────────────────────────────────────────────────────
// Employee view: my comp-offs
// ─────────────────────────────────────────────────────────────────────────
export function MyCompOffCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/api/comp-off/my')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  if (loading) return <Skeleton className="h-32" />;
  if (!data) return null;

  const all = [...(data.available || []), ...(data.redeemed || []), ...(data.converted || [])]
    .sort((a, b) => (b.earned_date || '').localeCompare(a.earned_date || ''));

  return (
    <Card className="border-slate-200" data-testid="my-comp-off-card">
      <CardHeader className="py-3 px-4 pb-2 flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Gift className="w-4 h-4 text-emerald-600" /> My Comp-Offs
          </CardTitle>
          <p className="text-[11px] text-slate-400 mt-1">Earned by working ≥ 8 hours on Sunday/holiday. Only HR can redeem on your behalf.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="my-comp-off-available">{data.summary?.available || 0} Available</Badge>
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{data.summary?.redeemed || 0} Redeemed</Badge>
          <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">{data.summary?.converted || 0} Converted</Badge>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {all.length === 0 ? (
          <p className="text-xs text-slate-400 italic py-4 text-center">No comp-offs yet. Work ≥ 8 hours on a Sunday or holiday to earn one.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs">Earned Date</TableHead>
                <TableHead className="text-xs">Reason</TableHead>
                <TableHead className="text-xs">Hours</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs">Redeemed / Converted</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {all.map(c => (
                <TableRow key={c.id} data-testid={`my-comp-off-row-${c.id}`}>
                  <TableCell className="text-xs font-tabular">{c.earned_date}</TableCell>
                  <TableCell className="text-xs text-slate-600">{c.earned_reason}</TableCell>
                  <TableCell className="text-xs font-tabular">{c.hours_worked}</TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_BADGE[c.status] || ''} text-[10px]`}>{STATUS_LABEL[c.status] || c.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {c.status === 'redeemed' && (
                      <span className="flex items-center gap-1"><ArrowRight className="w-3 h-3" />{c.redeemed_date} <span className="text-slate-400">by {c.redeemed_by_hr || '—'}</span></span>
                    )}
                    {c.status === 'converted_to_cl' && (
                      <span className="text-violet-600">+1 CL ({c.converted_for_month})</span>
                    )}
                    {c.status === 'available' && <span className="text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// HR/Admin view: all comp-offs + redeem dialog
// ─────────────────────────────────────────────────────────────────────────
export function HRCompOffPanel() {
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('available');
  const [redeemTarget, setRedeemTarget] = useState(null);
  const [redeemDate, setRedeemDate] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [scanRange, setScanRange] = useState({ start: '', end: '' });
  const [scanRunning, setScanRunning] = useState(false);
  const [convertMonth, setConvertMonth] = useState({ year: new Date().getFullYear(), month: new Date().getMonth() || 12 });
  const [convertRunning, setConvertRunning] = useState(false);
  const [convLog, setConvLog] = useState([]);

  const load = () => {
    setLoading(true);
    const q = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
    Promise.all([
      api.get(`/api/comp-off/all${q}`),
      api.get('/api/comp-off/conversion-log'),
    ])
      .then(([a, b]) => {
        setData(a.data || { rows: [], summary: {} });
        setConvLog(b.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };
  useEffect(load, [statusFilter]);

  const openRedeem = (row) => {
    setRedeemTarget(row);
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
    setRedeemDate(tomorrow.toISOString().slice(0, 10));
  };

  const handleRedeem = async () => {
    if (!redeemTarget || !redeemDate) return;
    setRedeeming(true);
    try {
      await api.post('/api/comp-off/redeem', { comp_off_id: redeemTarget.id, redeem_date: redeemDate });
      toast.success(`Comp-off redeemed for ${redeemTarget.employee_name} on ${redeemDate}`);
      setRedeemTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Redeem failed');
    } finally {
      setRedeeming(false);
    }
  };

  const handleScan = async () => {
    if (!scanRange.start || !scanRange.end) {
      toast.error('Pick start and end date');
      return;
    }
    setScanRunning(true);
    try {
      const r = await api.post('/api/comp-off/scan-and-create', { start_date: scanRange.start, end_date: scanRange.end });
      toast.success(`Scan complete: ${r.data.created} comp-offs created from ${r.data.candidates} attendance rows.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanRunning(false);
    }
  };

  const handleConvert = async () => {
    if (!window.confirm(`Run auto-convert for ${convertMonth.year}-${String(convertMonth.month).padStart(2, '0')}? Employees who took ZERO CL/EL/SL that month will have their comp-offs added to CL balance.`)) return;
    setConvertRunning(true);
    try {
      const r = await api.post('/api/comp-off/convert-now', { year: convertMonth.year, month: convertMonth.month });
      toast.success(`Converted ${r.data.total_comp_offs_converted} comp-offs across ${r.data.converted_employees} employees. Skipped: ${r.data.skipped_employees}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Convert failed');
    } finally {
      setConvertRunning(false);
    }
  };

  if (loading) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-4" data-testid="hr-comp-off-panel">
      {/* Summary + Filters */}
      <Card className="border-slate-200">
        <CardHeader className="py-3 px-4 pb-2 flex flex-row items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Gift className="w-4 h-4 text-emerald-600" /> Comp-Off Management (HR)
            </CardTitle>
            <p className="text-[11px] text-slate-400 mt-1">Earned auto on Sunday/holiday work ≥ 8 hrs. You can redeem on behalf of employees, or auto-convert to CL at month-end.</p>
          </div>
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">{data.summary?.total || 0} Total</Badge>
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="hr-comp-off-available">{data.summary?.available || 0} Available</Badge>
            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">{data.summary?.redeemed || 0} Redeemed</Badge>
            <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">{data.summary?.converted || 0} Converted</Badge>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="comp-off-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="redeemed">Redeemed</SelectItem>
                <SelectItem value="converted_to_cl">Converted to CL</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {data.rows.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-6 text-center">No comp-offs match the current filter.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Employee</TableHead>
                  <TableHead className="text-xs">Department</TableHead>
                  <TableHead className="text-xs">Earned Date</TableHead>
                  <TableHead className="text-xs">Reason</TableHead>
                  <TableHead className="text-xs">Hours</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Redeem / Notes</TableHead>
                  <TableHead className="text-xs text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map(r => (
                  <TableRow key={r.id} data-testid={`hr-comp-off-row-${r.id}`}>
                    <TableCell>
                      <p className="text-sm font-medium">{r.employee_name}</p>
                      <p className="text-[10px] text-slate-400 font-mono">{r.employee_code}</p>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{r.department}</TableCell>
                    <TableCell className="text-xs font-tabular">{r.earned_date}</TableCell>
                    <TableCell className="text-xs text-slate-600">{r.earned_reason}</TableCell>
                    <TableCell className="text-xs font-tabular">{r.hours_worked}</TableCell>
                    <TableCell><Badge className={`${STATUS_BADGE[r.status] || ''} text-[10px]`}>{STATUS_LABEL[r.status] || r.status}</Badge></TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.status === 'redeemed' && (<><span className="font-tabular">{r.redeemed_date}</span> <span className="text-slate-400">({r.redeemed_by_hr})</span></>)}
                      {r.status === 'converted_to_cl' && <span className="text-violet-600">{r.converted_for_month}</span>}
                      {r.status === 'available' && <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.status === 'available' ? (
                        <Button size="sm" className="h-7 bg-[#0D2850] hover:bg-[#091B36] gap-1 text-xs" onClick={() => openRedeem(r)} data-testid={`redeem-btn-${r.id}`}>
                          <Calendar className="w-3 h-3" />Redeem
                        </Button>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Manual Tools */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><Hourglass className="w-4 h-4 text-slate-500" /> Backfill Scan</CardTitle>
            <p className="text-[11px] text-slate-400">Re-scan a date range and create comp-offs for any Sunday/holiday work ≥ 8 hrs that wasn't auto-detected.</p>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-slate-500">Start date</Label>
                <Input type="date" value={scanRange.start} onChange={e => setScanRange({ ...scanRange, start: e.target.value })} className="h-8 text-sm" data-testid="scan-start" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">End date</Label>
                <Input type="date" value={scanRange.end} onChange={e => setScanRange({ ...scanRange, end: e.target.value })} className="h-8 text-sm" data-testid="scan-end" />
              </div>
            </div>
            <Button size="sm" onClick={handleScan} disabled={scanRunning} className="bg-[#0D2850] hover:bg-[#091B36] gap-1 h-8 w-full" data-testid="scan-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${scanRunning ? 'animate-spin' : ''}`} />{scanRunning ? 'Scanning...' : 'Scan & Create'}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><RefreshCw className="w-4 h-4 text-slate-500" /> Manual Auto-Convert</CardTitle>
            <p className="text-[11px] text-slate-400">Run the month-end conversion now (auto-runs 1st of every month at 2 AM IST). Employees with zero CL/EL/SL get comp-offs added to CL.</p>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-slate-500">Year</Label>
                <Input type="number" value={convertMonth.year} onChange={e => setConvertMonth({ ...convertMonth, year: parseInt(e.target.value) || 2026 })} className="h-8 text-sm" data-testid="convert-year" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">Month</Label>
                <Input type="number" min="1" max="12" value={convertMonth.month} onChange={e => setConvertMonth({ ...convertMonth, month: parseInt(e.target.value) || 1 })} className="h-8 text-sm" data-testid="convert-month" />
              </div>
            </div>
            <Button size="sm" onClick={handleConvert} disabled={convertRunning} className="bg-violet-600 hover:bg-violet-700 gap-1 h-8 w-full" data-testid="convert-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${convertRunning ? 'animate-spin' : ''}`} />{convertRunning ? 'Converting...' : 'Run Convert Now'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Log */}
      {convLog.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader className="py-3 px-4 pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2"><History className="w-4 h-4 text-slate-500" /> Conversion History</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Run At</TableHead>
                  <TableHead className="text-xs">For Month</TableHead>
                  <TableHead className="text-xs text-center">Earners</TableHead>
                  <TableHead className="text-xs text-center">Converted Emp</TableHead>
                  <TableHead className="text-xs text-center">Skipped Emp</TableHead>
                  <TableHead className="text-xs text-center">Comp-Offs Converted</TableHead>
                  <TableHead className="text-xs">Triggered By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {convLog.slice(0, 20).map((c, i) => (
                  <TableRow key={i} data-testid={`conv-log-${i}`}>
                    <TableCell className="text-xs font-tabular">{(c.ran_at || '').replace('T', ' ').slice(0, 16)}</TableCell>
                    <TableCell className="text-xs"><Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px] font-mono">{c.month_prefix}</Badge></TableCell>
                    <TableCell className="text-xs text-center font-tabular">{c.earner_count ?? 0}</TableCell>
                    <TableCell className="text-xs text-center font-tabular text-violet-700 font-medium">{c.converted_employees ?? 0}</TableCell>
                    <TableCell className="text-xs text-center font-tabular text-slate-400">{c.skipped_employees ?? 0}</TableCell>
                    <TableCell className="text-xs text-center font-tabular text-emerald-700 font-medium">{c.total_comp_offs_converted ?? 0}</TableCell>
                    <TableCell className="text-xs text-slate-500">{c.triggered_by || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Redeem dialog */}
      <Dialog open={!!redeemTarget} onOpenChange={v => { if (!v) setRedeemTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="redeem-dialog">
          <DialogHeader>
            <DialogTitle>Redeem Comp-Off</DialogTitle>
            <DialogDescription>
              Pick the future date when <strong>{redeemTarget?.employee_name}</strong> will use their comp-off (earned on {redeemTarget?.earned_date}). Their attendance for that date will auto-mark as paid Comp-Off leave.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs text-slate-500">Redeem Date (must be in the future)</Label>
            <Input type="date" value={redeemDate} onChange={e => setRedeemDate(e.target.value)} className="mt-1" data-testid="redeem-date-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedeemTarget(null)}>Cancel</Button>
            <Button onClick={handleRedeem} disabled={redeeming || !redeemDate} className="bg-[#0D2850] hover:bg-[#091B36]" data-testid="confirm-redeem">
              {redeeming ? 'Redeeming...' : 'Confirm Redeem'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Combined tab — shows HR panel for HR/admin, employee card otherwise
// ─────────────────────────────────────────────────────────────────────────
export default function CompOffTab() {
  const showHR = isAdmin() || isHR();
  return (
    <div className="space-y-4" data-testid="comp-off-tab">
      {showHR ? <HRCompOffPanel /> : <MyCompOffCard />}
    </div>
  );
}
